import { FormEvent, useCallback, useRef, useState, KeyboardEvent } from 'react';
import { SendHorizonalIcon } from 'lucide-react';
import classnames from 'classnames';

import styles from './add-comment.module.scss';

type Props = {
    sendOnEnter?: boolean;
    onCreate: (content: string) => void;
};

const AddComment = ({ sendOnEnter = false, onCreate }: Props) => {
    const editableRef = useRef<HTMLDivElement | null>(null);

    const [content, setContent] = useState('');
    const [holdingKey, setHoldingKey] = useState('');

    const sanitizeContent: (content: string) => string = useCallback(
        (content) => {
            return content.trim();
        },
        []
    );

    const handleInput: (ev: FormEvent<HTMLDivElement>) => void = useCallback(
        (ev) => {
            const content = (ev.target as HTMLDivElement).innerText;
            if (!content) return;
            setContent(sanitizeContent(content));
        },
        []
    );

    const handlePress: () => void = useCallback(() => {
        onCreate(sanitizeContent(content));
        clearContent();
    }, [content, onCreate]);

    const clearContent = () => {
        editableRef.current!.textContent = '';
        setContent('');
    };

    const isDisabled: () => boolean = useCallback(() => {
        return !content.trim().length;
    }, [content]);

    const pressToSend = useCallback(
        (ev: KeyboardEvent<HTMLDivElement>) => {
            if (!sendOnEnter) return;
            if (ev.key.startsWith('Shift')) {
                setHoldingKey('Shift');
                return;
            }
            if (ev.key === 'Enter' && holdingKey !== 'Shift') {
                ev.preventDefault();
                handlePress();
            }
        },
        [holdingKey, handlePress, content]
    );

    return (
        <div className={styles.composer}>
            <div className={styles.content}>
                <div
                    ref={editableRef}
                    className={styles.editable}
                    spellCheck={true}
                    contentEditable={true}
                    onInput={handleInput}
                    onKeyDown={pressToSend}
                    onKeyUp={(ev) => {
                        if (!sendOnEnter) return;
                        if (ev.key.startsWith('Shift')) setHoldingKey('');
                    }}
                />
                <span
                    className={classnames({
                        [styles.placeholder]: true,
                        [styles.hidden]: content.length > 0,
                    })}
                >
                    Escribe tu comentario aquí
                </span>
            </div>
            <div className={styles.footer}>
                <button onClick={handlePress} disabled={isDisabled()}>
                    <SendHorizonalIcon size={18} />
                </button>
            </div>
        </div>
    );
};

export default AddComment;
